import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Phone, Send, Inbox, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DashboardHeader } from "@/components/DashboardHeader";
import { waLink, telLink, SITE } from "@/lib/site";

const StatusPill = ({ services }) => (
  <span className="inline-flex items-center rounded-full bg-slate-100 text-slate-700 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
    {services || "Quote request"}
  </span>
);

export const Dashboard = () => {
  const { user } = useAuth();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/me/enquiries");
        setEnquiries(data);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div data-testid="customer-dashboard" className="min-h-screen bg-[#F8FAFC]">
      <DashboardHeader
        title={`Hi ${user?.name?.split(" ")[0] || "there"} 👋`}
        subtitle="Here are your past enquiries and quick actions."
      />

      <div className="max-w-6xl mx-auto px-6 lg:px-8 pb-16">
        <div className="grid md:grid-cols-3 gap-4">
          <Link to="/#contact" data-testid="dashboard-new-quote"
                className="group rounded-3xl bg-[#0A192F] text-white p-6 flex items-center justify-between hover:bg-[#0F172A] transition-all">
            <div>
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-white/70">Start</div>
              <div className="mt-1 font-display font-semibold text-xl">Request a new quote</div>
            </div>
            <ArrowRight className="h-6 w-6 group-hover:translate-x-1 transition-transform" />
          </Link>
          <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="dashboard-wa"
             className="rounded-3xl bg-white border border-slate-200 p-6 flex items-center gap-4 hover-lift">
            <span className="h-12 w-12 rounded-2xl bg-[#25D366] text-white inline-flex items-center justify-center"><MessageCircle className="h-5 w-5" /></span>
            <div>
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">WhatsApp</div>
              <div className="mt-1 font-display font-semibold text-[#0A192F]">{SITE.phone}</div>
            </div>
          </a>
          <a href={telLink} data-testid="dashboard-call"
             className="rounded-3xl bg-white border border-slate-200 p-6 flex items-center gap-4 hover-lift">
            <span className="h-12 w-12 rounded-2xl bg-[#0A192F] text-white inline-flex items-center justify-center"><Phone className="h-5 w-5" /></span>
            <div>
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Call us</div>
              <div className="mt-1 font-display font-semibold text-[#0A192F]">{SITE.phone}</div>
            </div>
          </a>
        </div>

        <section className="mt-10">
          <div className="flex items-end justify-between">
            <h2 className="font-display font-semibold text-2xl text-[#0A192F]">Your enquiries</h2>
            <span className="text-sm text-slate-500">{enquiries.length} total</span>
          </div>

          <div className="mt-5 rounded-3xl bg-white border border-slate-200 overflow-hidden">
            {loading ? (
              <div className="p-10 text-center text-slate-500">Loading…</div>
            ) : enquiries.length === 0 ? (
              <div data-testid="dashboard-empty-state" className="p-12 text-center">
                <span className="inline-flex h-14 w-14 rounded-2xl bg-slate-100 text-slate-500 items-center justify-center">
                  <Inbox className="h-6 w-6" />
                </span>
                <h3 className="mt-4 font-display font-semibold text-xl text-[#0A192F]">No enquiries yet</h3>
                <p className="mt-2 text-slate-600">Submit your first quote request and it will appear here.</p>
                <Link to="/#contact" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#0A192F] text-white px-6 py-3 font-semibold hover:bg-[#0F172A]">
                  <Send className="h-4 w-4" /> Request a quote
                </Link>
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {enquiries.map((e) => (
                  <li key={e.id} data-testid={`dashboard-enquiry-${e.id}`} className="p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <StatusPill services={e.services} />
                        <span className="text-xs text-slate-500">
                          {new Date(e.created_at).toLocaleDateString("en-GB", { day:"numeric", month:"short", year:"numeric" })}
                        </span>
                      </div>
                      <div className="mt-2 font-display font-semibold text-[#0A192F] truncate">
                        {e.moving_from || "—"} <span className="text-slate-400 mx-1">→</span> {e.moving_to || "—"}
                      </div>
                      {e.message && <p className="mt-1 text-sm text-slate-600 line-clamp-2">{e.message}</p>}
                    </div>
                    <div className="text-sm text-slate-500 whitespace-nowrap">
                      {e.preferred_date && <>Date: <span className="text-[#0A192F] font-medium">{e.preferred_date}</span></>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};
